// Shared helpers for translating raw hermes_email_queue_jobs status into a
// display-friendly status that distinguishes "skipped because the recipient is
// suppressed (bounced / unsubscribed / complained)" from a real provider
// failure.
//
// Background: PR #504 fixed the circuit-breaker to exclude suppression
// rejections from the failure-rate calc. But the per-job status in
// hermes_email_queue_jobs is still written as 'failed' for suppression cases
// — same status as a real provider failure. The campaign UI was showing it as
// a generic "Failed" badge, which surprises users (they think the send broke
// when really the recipient just unsubscribed).
//
// Detection signal: a suppression-rejected job has
//   status = 'failed'
//   error_message LIKE 'Recipient email status is%'
//
// Source of truth for the error_message prefix:
// supabase/functions/email-queue-processor/index.ts (the suppression gate
// writes exactly: `Recipient email status is '${status}' — cannot send`).

export type JobDisplayStatus =
  | 'pending'
  | 'processing'
  | 'sent'
  | 'failed'
  | 'retrying'
  | 'skipped-suppressed';

export type SuppressionReason = 'bounced' | 'unsubscribed' | 'complained';

const SUPPRESSION_PREFIX = 'Recipient email status is';

/**
 * Returns the display-friendly status for a queue job. When the underlying
 * status is 'failed' AND the error_message matches the suppression-gate
 * signature, we surface 'skipped-suppressed' so the UI can render a calmer
 * badge instead of a red "Failed" pill.
 */
export function getJobDisplayStatus(job: {
  status: string;
  error_message?: string | null;
}): JobDisplayStatus {
  if (
    job.status === 'failed' &&
    typeof job.error_message === 'string' &&
    job.error_message.startsWith(SUPPRESSION_PREFIX)
  ) {
    return 'skipped-suppressed';
  }
  return job.status as JobDisplayStatus;
}

/**
 * Extracts the specific suppression reason (bounced / unsubscribed /
 * complained) from a queue-job error_message. Returns null for non-suppression
 * messages.
 */
export function parseSuppressionReason(
  errorMessage: string | null | undefined,
): SuppressionReason | null {
  if (!errorMessage) return null;
  const m = errorMessage.match(
    /Recipient email status is '(bounced|unsubscribed|complained)'/,
  );
  return m ? (m[1] as SuppressionReason) : null;
}

/**
 * Computes the three counters used by the campaign-level summary. The split
 * matters because real "failed" sends are actionable (retryable, may indicate
 * provider trouble) while "skipped (suppressed)" sends are not (the recipient
 * just can't receive marketing email). Lumping them together caused the
 * silent-failure UX problem PR #504 hinted at but didn't finish.
 */
export interface CampaignJobCounts {
  sent: number;
  /** Pending + processing + retrying — anything not yet terminal. */
  pending: number;
  /** Suppression rejections (deterministic shape; see file header). */
  skipped: number;
  /** Real failures — status='failed' minus the suppression subset. */
  failed: number;
  total: number;
}

export function computeCampaignJobCounts(
  jobs: ReadonlyArray<{ status: string; error_message?: string | null }>,
): CampaignJobCounts {
  let sent = 0;
  let pending = 0;
  let skipped = 0;
  let failed = 0;
  for (const job of jobs) {
    const display = getJobDisplayStatus(job);
    switch (display) {
      case 'sent':
        sent += 1;
        break;
      case 'skipped-suppressed':
        skipped += 1;
        break;
      case 'failed':
        failed += 1;
        break;
      // pending / processing / retrying all roll into "pending" for UI
      // purposes — they're all "not yet terminal".
      case 'pending':
      case 'processing':
      case 'retrying':
        pending += 1;
        break;
      default:
        pending += 1;
        break;
    }
  }
  return { sent, pending, skipped, failed, total: jobs.length };
}

/**
 * Human-readable label for the suppression reason used in the per-job badge.
 */
export function suppressionReasonLabel(reason: SuppressionReason | null): string {
  switch (reason) {
    case 'unsubscribed':
      return 'unsubscribed';
    case 'bounced':
      return 'bounced';
    case 'complained':
      return 'complained';
    default:
      return 'suppressed';
  }
}
